import { Core } from "@faire/design-tokens";
import { ISO3166Alpha3 } from "@faire/web-api/indigofair/iso3166/ISO3166Alpha3";
import { WebApiError } from "@faire/web-api/WebApiError";
import { COUNTRY } from "@faire/web/common/consts/COUNTRY";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import Checkbox from "@faire/web/slate/Checkbox/index";
import { Input } from "@faire/web/slate/Input";
import { Dropdown } from "@faire/web/slate/Input/Dropdown";
import { PhoneInput } from "@faire/web/slate/Input/PhoneInput";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { getAddressSchema } from "@faire/web/ui/Addresses/addressSchema";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Form, Formik, FormikHelpers } from "formik";
import React from "react";
import styled from "styled-components";

import { AutoCompleteStoreName } from "@faire/retailer/features/ProspectingWithSamples/AutoCompleteStoreName";
import { SampleForm } from "@faire/retailer/features/ProspectingWithSamples/ProspectingSampleSignupFlow";
import { StateOrProvince } from "@faire/retailer/features/ProspectingWithSamples/StateOrProvince";
import { useFormValidation } from "@faire/retailer/features/ProspectingWithSamples/useFormValidation";

interface ShippingDetailsProps {
  initialValues: SampleForm;
  setFormValues: (formValues: SampleForm) => void;
  onSubmit: (
    formValue: SampleForm,
    formikHelpers: FormikHelpers<SampleForm>
  ) => void;
  apiCallFailed: boolean;
  apiCallIsLoading: boolean;
  apiCallError: WebApiError | undefined;
}

export const ShippingDetails: React.FC<ShippingDetailsProps> = ({
  initialValues,
  setFormValues,
  onSubmit,
  apiCallFailed,
  apiCallIsLoading,
  apiCallError,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { validateForm } = useFormValidation();
  const localizedErrorMessage = useLocalizedErrorMessage(apiCallError);

  return (
    <Container>
      <Typography variant="displaySSerifRegular" align="center">
        <StrictLocalMsg
          defaultMessage="Add your shipping details"
          description={{
            text: "Title of the shipping details form",
            img: "https://cdn.faire.com/fastly/e59bb3058399d9c1414e1382d71a4ddae80e242c8e7143f95dc84df0f7b37188.png",
          }}
        />
      </Typography>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validateForm}
      >
        {({
          values,
          handleChange,
          setFieldValue,
          errors,
          touched,
          isSubmitting,
        }) => {
          const addressSchema = getAddressSchema(
            values.country,
            strictLocalize
          );

          return (
            <StyledForm>
              <Input
                autoFocus
                label={strictLocalize({
                  defaultMessage: "First Name",
                  description: {
                    text: "User's first/given name. Text should be all capitalized",
                    img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
                  },
                })}
                type="text"
                name="firstName"
                error={touched.firstName && errors.firstName}
                value={values.firstName}
                onChange={handleChange}
              />
              <Input
                label={strictLocalize({
                  defaultMessage: "Last Name",
                  description: {
                    text: "User's last name. Text should be all capitalized",
                    img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
                  },
                })}
                type="text"
                name="lastName"
                error={touched.lastName && errors.lastName}
                value={values.lastName}
                onChange={handleChange}
              />
              <Dropdown
                label={strictLocalize({
                  defaultMessage: "Country",
                  description: {
                    text: "Label for the input where user enters their country.",
                    img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
                  },
                })}
                options={COUNTRY(strictLocalize)}
                onSelect={(country) => {
                  setFieldValue("country", country.value);
                  setFormValues({ ...values, country: country.value });
                }}
                value={values.country}
              />
              <AutoCompleteStoreName
                formValues={values}
                setFieldValue={setFieldValue}
                setFormValues={setFormValues}
                error={touched.storeName && errors.storeName}
              />
              <Input
                label={
                  addressSchema.labels["address1"] ??
                  strictLocalize({
                    defaultMessage: "Street",
                    description: {
                      text: "Label for the input where user enters their street address",
                      img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
                    },
                  })
                }
                type="text"
                name="address1"
                error={touched.address1 && errors.address1}
                value={values.address1}
                onChange={handleChange}
              />
              <Input
                label={
                  addressSchema.labels["address2"] ??
                  strictLocalize({
                    defaultMessage: "Apt, Suite (optional)",
                    description: {
                      text: "Label for the input where user enters their second line of the street address",
                      img: "https://cdn.faire.com/fastly/4c4b16cccf562f916c0973edbfa5e8fdeb175857b2103ef3bda54f66bfed592c.png",
                    },
                  })
                }
                type="text"
                name="address2"
                error={touched.address2 && errors.address2}
                value={values.address2}
                onChange={handleChange}
              />
              <Input
                label={
                  addressSchema.labels["city"] ??
                  strictLocalize({
                    defaultMessage: "City",
                    description: {
                      text: "Label for the input where user enters their city",
                      img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
                    },
                  })
                }
                type="text"
                name="city"
                error={touched.city && errors.city}
                value={values.city}
                onChange={handleChange}
              />
              <StateOrProvince
                shouldRender={addressSchema.required_fields.includes("state")}
                label={
                  addressSchema.labels["state"] ??
                  strictLocalize({
                    defaultMessage: "State",
                    description: {
                      text: "User's state",
                      img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
                    },
                  })
                }
                country={values.country}
                error={touched.state && errors.state}
                value={values.state}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                setFormValues={setFormValues}
                formValues={values}
              />
              <Input
                label={
                  addressSchema.labels["postal_code"] ??
                  strictLocalize({
                    defaultMessage: "Zipcode",
                    description: {
                      text: "User's zip/postal code",
                      img: "https://cdn.faire.com/fastly/f9b8788b5369f0d4234fdf46c586679c9d077fea8a5e0452318468ee45b86c86.png",
                    },
                  })
                }
                type="text"
                name="zipcode"
                error={touched.zipcode && errors.zipcode}
                value={values.zipcode}
                onChange={handleChange}
              />
              <PhoneInput
                aria-label={strictLocalize({
                  defaultMessage: "Phone Number",
                  description: {
                    text: "User's phone number",
                    img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
                  },
                })}
                label={strictLocalize({
                  defaultMessage: "Phone Number",
                  description: {
                    text: "User's phone number",
                    img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
                  },
                })}
                name="phoneNumber"
                value={values.phoneNumber}
                defaultPhoneNumber={values.phoneNumber}
                defaultCountry={values.country as ISO3166Alpha3}
                onChangePhoneNumber={(inputValue) => {
                  setFieldValue("phoneNumber", inputValue);
                  setFormValues({ ...values, phoneNumber: inputValue });
                }}
                error={touched.phoneNumber && errors.phoneNumber}
                placeholder={strictLocalize({
                  defaultMessage: "Phone Number",
                  description: {
                    text: "User's phone number",
                    img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
                  },
                })}
              />
              <Checkbox
                label={strictLocalize({
                  defaultMessage: "Sign up to get email notifications",
                  description: {
                    text: "Label for the checkbox that user can check to opt into email notifications",
                    img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
                  },
                })}
                aria-label={strictLocalize({
                  defaultMessage: "Sign up to get email notifications",
                  description: {
                    text: "Label for the checkbox that user can check to opt into email notifications",
                    img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
                  },
                })}
                checked={values.optIntoEmailNotifications}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue("optIntoEmailNotifications", e.target.checked);
                  setFormValues({
                    ...values,
                    optIntoEmailNotifications: e.target.checked,
                  });
                }}
              />
              <StyledButton
                type="submit"
                disabled={isSubmitting || apiCallIsLoading}
                loading={isSubmitting || apiCallIsLoading}
              >
                <StrictLocalMsg
                  defaultMessage="Submit"
                  description={{
                    text: "Title of the submit button that will submit the sample request form.",
                    img: "https://cdn.faire.com/fastly/21f3bda64fd506304c26e0058085153be907e7fd8d10e5fe6cb348e425e01ac5.png",
                  }}
                />
              </StyledButton>
              {apiCallFailed && !apiCallIsLoading && !isSubmitting ? (
                <Typography color={Core.text.critical}>
                  {localizedErrorMessage}
                </Typography>
              ) : null}
            </StyledForm>
          );
        }}
      </Formik>
    </Container>
  );
};

const useLocalizedErrorMessage = (error: WebApiError | undefined) => {
  const { strictLocalize } = useStrictLocalization();
  if (!error || !error.message) {
    return null;
  }
  if (error.message === "Business name is required.") {
    return strictLocalize({
      defaultMessage: "Business name is required.",
      description: {
        text: "Error message that will be displayed if the user does not enter a business name.",
        img: "https://cdn.faire.com/fastly/5dd387174ab161827054315fadabf8409e910821a3ccce00a27f88810d440f92.png",
      },
    });
  }
  return strictLocalize({
    defaultMessage: "Please check your information and try again.",
    description: {
      text: "Generic error message that will be displayed if the api call fails.",
      img: "https://cdn.faire.com/fastly/4c68eca475a7aa1a4812eebbb2bf55c25ebc5ccc7fac00cc701d775af05013f0.png",
    },
  });
};

const Container = styled(Column).attrs({
  gap: "4x",
})`
  width: 100%;
`;

const StyledForm = styled(Form)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${getSpacing("3x")};
`;

const StyledButton = styled(Button)`
  width: 100%;
`;
